import { useState } from 'react';
import { Book, Globe, Clock, FileCheck, Star, Award, Shield, UserCheck, CheckCircle } from 'lucide-react';
import HighlightText from '../components/core/HomePage/HighlightText';
import Footer from '../components/common/Footer';

export default function PaperPublicationSupport() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('');
  const [budget, setBudget] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log({ name, email, phone, service, budget, message });
  };

  return (
    <div className="min-h-screen bg-richblue-5 text-richblue-700">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Article/Research Paper Writing & Publication</h1>
          <p className="text-xl text-richblue-500">
            Top-notch paper writing and publication services with guaranteed quality and expert guidance
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">What is Research Paper/Article Writing & <HighlightText text={"Publication"} /></h2>
            <p className="text-richblue-500 mb-8 text-lg">
              This service is for researchers who wish to collaborate with us in paper writing, editing, and publishing their research paper in a recognized international journal. We aim to help you publish your research paper/article in a recognized journal within the shortest possible time.
            </p>
            
            <div className="bg-richblue-5 p-8 rounded-lg shadow-md border border-richblue-300 mb-12">
              <h3 className="text-2xl font-bold mb-4 text-richblue-600">We are specialized in</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">National & International indexed journals (UGC care, SCOPUS, Web of Science, SCI)</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Hassle-free publication process</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Expert topic selection guidance</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Professional editing and formatting</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Publication <HighlightText text={"Process"} /></h2>
          
          <div className="max-w-4xl mx-auto">
            {/* Process Step 1 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Submission of Proposal</h3>
                <p className="text-richblue-500">
                  The Researcher submits their Proposal and relevant materials for review and processing.
                </p>
              </div>
            </div>

            {/* Process Step 2 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Professional Editing</h3>
                <p className="text-richblue-500">
                  Our editors review and edit the proposal to ensure it meets all requirements for publication in recognized journals.
                </p>
              </div>
            </div>

            {/* Process Step 3 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Journal Selection</h3>
                <p className="text-richblue-500">
                  We recommend appropriate journals based on your research area and requirements.
                </p>
              </div>
            </div>

            {/* Process Step 4 */}
            <div className="flex">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Submission & Follow-up</h3>
                <p className="text-richblue-500">
                  We handle the submission process and follow up with journals, including revisions based on reviewer comments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Why Choose <HighlightText text={"Us"} /></h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <CheckCircle className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Payment as per Progress</h3>
              <p className="text-richblue-500">
                Flexible payment options based on project milestones and progress.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Star className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">100% Authentic Work</h3>
              <p className="text-richblue-500">
                Guaranteed original content with proper citations and references.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Clock className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">On-time Delivery</h3>
              <p className="text-richblue-500">
                Strict adherence to deadlines with regular progress updates.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Shield className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Privacy Guaranteed</h3>
              <p className="text-richblue-500">
                Complete confidentiality of your research and personal information.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Award className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Quality Assurance</h3>
              <p className="text-richblue-500">
                Multiple rounds of review and quality checks before submission.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Globe className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Global Reach</h3>
              <p className="text-richblue-500">
                Access to international journals and publication platforms.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-richblue-600">Get Started Today</h2>
              <p className="text-richblue-500 mb-6">
                Connect with our publication experts for a free consultation about your research paper needs.
              </p>
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 mb-6">
                <h3 className="text-xl font-bold mb-4 text-richblue-600">Our Support</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="text-richblue-400 mr-3 mt-1">★</div>
                    <div>
                      <p className="font-semibold text-richblue-600">Expert Guidance</p>
                      <p className="text-richblue-500 text-sm">Professional support throughout the publication process</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-richblue-400 mr-3 mt-1">★</div>
                    <div>
                      <p className="font-semibold text-richblue-600">Quality Assurance</p>
                      <p className="text-richblue-500 text-sm">Thorough review and editing of your research paper</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-richblue-400 mr-3 mt-1">★</div>
                    <div>
                      <p className="font-semibold text-richblue-600">Timely Updates</p>
                      <p className="text-richblue-500 text-sm">Regular progress updates and communication</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">Request Information</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-richblue-500 mb-2">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-richblue-500 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-richblue-500 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-richblue-500 mb-2">Select Service</label>
                  <select
                    id="service"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  >
                    <option value="">Select a Service</option>
                    <option value="writing">Research Paper Writing</option>
                    <option value="publication">Research Paper Publication</option>
                    <option value="both">Both - Paper Writing & Publication</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="budget" className="block text-richblue-500 mb-2">Select Budget</label>
                  <select
                    id="budget"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  >
                    <option value="">Select Budget Range</option>
                    <option value="30k-50k">30K to 50K (Quality A)</option>
                    <option value="50k-1lac">50K to 1 Lac (Quality A+)</option>
                    <option value="flexible">Budget is not an issue (Quality A++)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-richblue-500 mb-2">Project Details</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please describe your research paper requirements, timeline, and any specific assistance needed."
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900 h-32"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-richblue-400 text-white font-semibold py-3 px-6 rounded-md hover:bg-richblue-500 transition-all duration-200"
                >
                  Request Information
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
} 